import { useLayoutEffect, useState } from "react";

type TargetElementState = {
  tourId: string;
  element: Element | null;
};

/** Resolves a `data-tourId="<tourId>"` selector to a live DOM element.
 *
 * Keeps a MutationObserver running for the whole step (not just until the
 * first match) so it re-resolves when the target either mounts late OR is
 * replaced — e.g. a parent that remounts it via a changing `key`
 * (DatasetRightAttrFilter is keyed on the selected dataset's metadata id). A
 * keyed remount detaches the cached node and inserts a fresh one; a watcher
 * that stopped at the first match would leave the spotlight/tooltip anchored
 * to the dead node. Owner of this hook must live above the children that
 * depend on `targetEl` so a single observer is shared.
 */
export function useTargetElement(tourId: string | undefined): Element | null {
  const [target, setTarget] = useState<TargetElementState | null>(null);

  useLayoutEffect(() => {
    if (!tourId) {
      setTarget(null);
      return;
    }

    const selector = `[data-tourid="${tourId}"]`;

    const resolve = () => {
      const next = document.querySelector(selector);
      // Skip the update when the resolved node is unchanged so unrelated DOM
      // mutations don't churn renders.
      setTarget((prev) =>
        prev?.tourId === tourId && prev.element === next
          ? prev
          : { tourId, element: next }
      );
    };

    resolve();

    const observer = new MutationObserver(resolve);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      // Some targets are long-lived nodes whose `data-tourid` flips to the
      // active step's id (the sidebar container mirrors the current step —
      // see SIDEBAR_SPOTLIGHT_TOUR_IDS). That's an attribute mutation, not a
      // childList one, so without this the observer never re-resolves and the
      // tour shows only the dimmed backdrop.
      attributes: true,
      attributeFilter: ["data-tourid"]
    });

    return () => {
      observer.disconnect();
    };
  }, [tourId]);

  return target && target.tourId === tourId ? target.element : null;
}
